import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { devLog } from './logger'; // 🛡️ Logs seguros
import { authFetch } from './api';

// -----------------------------------------------------------------------------
// Config de grupos vem do BACKEND (GET /api/grupos-ad/config), que lê a tabela
// grupos_ad. NÃO há listas de grupos hardcoded aqui — a fonte da verdade é o banco.
// A config é cacheada em sessionStorage apenas para evitar flicker entre telas
// (cache de dado do backend, não um fallback hardcoded).
// -----------------------------------------------------------------------------
const CONFIG_CACHE_KEY = 'grupos_ad_config';
const CONFIG_CHANGE_EVENT = 'prc:grupos-config-changed';
const SESSION_CHANGE_EVENT = 'prc:session-changed';

const EMPTY_CONFIG = {
  aprovadores: [],
  compras: [],
  consulta: [],
  dev: [],
  reqPrefixes: [],
  departamentoMap: {},
};

function readConfigCache() {
  try {
    const raw = sessionStorage.getItem(CONFIG_CACHE_KEY);
    if (raw) return { ...EMPTY_CONFIG, ...JSON.parse(raw) };
  } catch (e) {
    console.error('Erro ao ler config de grupos do cache', e);
  }
  return EMPTY_CONFIG;
}

function hasAuthToken() {
  try {
    return !!sessionStorage.getItem('access_token');
  } catch {
    return false;
  }
}

async function fetchConfig() {
  // Só busca quando autenticado. Na tela de login não há token e o /config
  // retornaria 401, que o interceptor global trata com clear()+redirect → loop infinito.
  if (!hasAuthToken()) return null;
  try {
    const cfg = await authFetch('/grupos-ad/config');
    if (cfg && typeof cfg === 'object') {
      const merged = { ...EMPTY_CONFIG, ...cfg };
      try { sessionStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(merged)); } catch {}
      window.dispatchEvent(new CustomEvent(CONFIG_CHANGE_EVENT));
      return merged;
    }
  } catch (e) {
    devLog('[RoleContext] Falha ao buscar config de grupos (mantém cache):', e?.message);
  }
  return null;
}

const RoleContext = createContext({ account: null, role: 'solicitante', roles: [], groups: [], departamento: null, departamentoOptions: [], todosCentrosCusto: [] });

function readUserData() {
  try {
    const userStr = sessionStorage.getItem('user');
    devLog('[RoleContext] Reading user from sessionStorage:', userStr);
    if (userStr) {
      const user = JSON.parse(userStr);
      devLog('[RoleContext] Parsed user:', user);
      return {
        account: user,
        roles: user.roles || [],
        groups: user.groups || []
      };
    }
  } catch (e) {
    console.error('Erro ao ler user do sessionStorage', e);
  }
  devLog('[RoleContext] No user found, returning default');
  return { account: null, roles: [] };
}

function normalizeGroupName(name) {
  // Normaliza caixa, remove espaços/underscores/hífens e diacríticos (acentos) para comparação robusta
  const base = (name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return base.replace(/[_\s-]+/g, '').toLowerCase();
}

function hasGroupMatch(groups = [], refs = []) {
  const normRefs = new Set((refs || []).map(normalizeGroupName));
  return (groups || []).some(g => normRefs.has(normalizeGroupName(g)));
}

function isComprasGroup(groups = [], cfg = EMPTY_CONFIG) {
  return hasGroupMatch(groups, cfg.compras);
}

function isConsultaComprasGroup(groups = [], cfg = EMPTY_CONFIG) {
  return hasGroupMatch(groups, cfg.consulta);
}

function isAprovadorGroup(groups = [], cfg = EMPTY_CONFIG) {
  return hasGroupMatch(groups, cfg.aprovadores);
}

function isDevGroup(groups = [], cfg = EMPTY_CONFIG) {
  return hasGroupMatch(groups, cfg.dev);
}

function isReqGroup(groups = [], cfg = EMPTY_CONFIG) {
  const prefixes = cfg.reqPrefixes || [];
  if (!prefixes.length) return false;
  return (groups || []).some((g) => {
    const n = normalizeGroupName(g);
    return prefixes.some((p) => n.startsWith(normalizeGroupName(p)));
  });
}

function determineRole(roles, groups, cfg) {
  // 1) Pelos grupos do Graph (prioridade para Compras na hierarquia funcional)
  if (isComprasGroup(groups || [], cfg)) return 'comprador';
  if (isAprovadorGroup(groups || [], cfg)) return 'aprovador';
  if (isConsultaComprasGroup(groups || [], cfg)) return 'consulta';

  // 2) Pelas roles do token (App Roles) como fallback
  const byRole = roles?.includes('aprovador') ? 'aprovador' : (roles?.includes('comprador') ? 'comprador' : null);
  return byRole || 'solicitante';
}

function getAccessFlags(roles = [], groups = [], cfg = EMPTY_CONFIG) {
  const isDev = (roles || []).includes('devs') || (roles || []).includes('Desenvolvedores') || isDevGroup(groups, cfg);
  const canAprovador = isAprovadorGroup(groups, cfg) || (roles || []).includes('aprovador') || isDev;
  const canComprador = isComprasGroup(groups, cfg) || (roles || []).includes('comprador') || isDev;
  const canConsulta = isConsultaComprasGroup(groups, cfg) || isDev;
  const canSolicitante = isReqGroup(groups, cfg) || isDev;
  const hasAccess = canSolicitante || canAprovador || canComprador || canConsulta || isDev;
  return { isDev, canSolicitante, canAprovador, canComprador, canConsulta, hasAccess };
}

function determineRoleAndDept(user, cfg = EMPTY_CONFIG) {
  if (!user) return { role: 'sem_acesso', departamento: null, departamentoOptions: [] };
  const groups = user.groups || [];

  // Papel principal exibido na UI (botões usam flags de acesso).
  let role = 'sem_acesso';
  if (isComprasGroup(groups, cfg)) role = 'comprador';
  else if (isAprovadorGroup(groups, cfg)) role = 'aprovador';
  else if (isConsultaComprasGroup(groups, cfg)) role = 'consulta';
  else if (isReqGroup(groups, cfg)) role = 'solicitante';

  // Departamento/Centro de Custo: coleta todos os grupos Req_* do usuário (via config)
  const departamentoMap = cfg.departamentoMap || {};
  const refs = Object.keys(departamentoMap);
  const opcoes = [];
  for (const g of groups) {
    const key = refs.find(ref => normalizeGroupName(g) === normalizeGroupName(ref));
    if (key) {
      const code = departamentoMap[key];
      if (code && !opcoes.includes(code)) opcoes.push(code);
    }
  }
  const departamento = opcoes.length ? opcoes[0] : null;
  return { role, departamento, departamentoOptions: opcoes };
}


export function RoleProvider({ children }) {
  const [userData, setUserData] = useState(readUserData());
  const [config, setConfig] = useState(readConfigCache());

  useEffect(() => {
    // Atualiza quando houver alterações de storage (outros tabs) e também no mesmo tab.
    const refresh = () => setUserData(readUserData());

    const onStorage = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const maybeFetchConfig = () => {
      // Busca a config de grupos apenas quando autenticado (evita 401 → loop na tela de login).
      if (!hasAuthToken()) return;
      fetchConfig().then((cfg) => { if (cfg) setConfig(cfg); });
    };
    const onSessionChanged = (event) => {
      const key = event?.detail?.key;
      if (!key || key === 'user' || key === 'msal.activeAccount' || key === 'access_token') {
        refresh();
        // Ao logar (token/usuário aparecem), carrega a config do backend.
        if (key === 'access_token' || key === 'user' || !key) maybeFetchConfig();
      }
    };
    const onConfigChanged = () => setConfig(readConfigCache());

    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', refresh);
    window.addEventListener(SESSION_CHANGE_EVENT, onSessionChanged);
    window.addEventListener(CONFIG_CHANGE_EVENT, onConfigChanged);

    // Patch único do sessionStorage para disparar eventos no mesmo tab.
    if (typeof window !== 'undefined' && !window.__prcSessionStoragePatched) {
      try {
        const _orig = {
          setItem:    sessionStorage.setItem.bind(sessionStorage),
          removeItem: sessionStorage.removeItem.bind(sessionStorage),
          clear:      sessionStorage.clear.bind(sessionStorage),
        };

        Object.assign(sessionStorage, {
          setItem(key, value) {
            _orig.setItem(key, value);
            window.dispatchEvent(new CustomEvent(SESSION_CHANGE_EVENT, { detail: { key } }));
          },
          removeItem(key) {
            _orig.removeItem(key);
            window.dispatchEvent(new CustomEvent(SESSION_CHANGE_EVENT, { detail: { key } }));
          },
          clear() {
            _orig.clear();
            window.dispatchEvent(new CustomEvent(SESSION_CHANGE_EVENT, { detail: { key: null } }));
          },
        });

        Object.defineProperty(window, '__prcSessionStoragePatched', { value: true, writable: false });
      } catch {
        // Ambientes que bloqueiam extensão de built-ins — degradação silenciosa
      }
    }

    refresh();
    // Busca a config de grupos do backend (fonte da verdade) — só se já autenticado.
    maybeFetchConfig();

    return () => {
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener(SESSION_CHANGE_EVENT, onSessionChanged);
      window.removeEventListener(CONFIG_CHANGE_EVENT, onConfigChanged);
    };
  }, []);

  const role = useMemo(() => determineRole(userData.roles, userData.groups, config), [userData.roles, userData.groups, config]);
  const deptInfo = useMemo(() => determineRoleAndDept({ groups: userData.groups || [] }, config), [userData.groups, config]);
  const accessFlags = useMemo(() => getAccessFlags(userData.roles || [], userData.groups || [], config), [userData.roles, userData.groups, config]);
  const resolvedRole = role === 'solicitante' && deptInfo.role !== 'solicitante' ? deptInfo.role : role;
  // Todos os centros de custo REGISTRADOS no sistema (grupos_ad), não só os do
  // usuário logado. Fonte de verdade para filtros de busca (ex.: Histórico
  // Geral) — que não devem depender de a página já ter carregado alguma
  // requisição daquele centro para ele aparecer como opção.
  const todosCentrosCusto = useMemo(
    () => [...new Set(Object.values(config.departamentoMap || {}))].sort(),
    [config]
  );
  const value = useMemo(() => ({
    ...userData,
    role: resolvedRole,
    departamento: deptInfo.departamento || null,
    departamentoOptions: deptInfo.departamentoOptions || [],
    todosCentrosCusto,
    ...accessFlags,
  }), [userData, resolvedRole, deptInfo, todosCentrosCusto, accessFlags]);

  devLog('[RoleContext] Final context value:', value);

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  return useContext(RoleContext);
}
